'use client';

import {
  LeadingAddOnSlot,
  LeadingAdornmentSlot,
  TrailingAddOnSlot,
  TrailingAdornmentSlot,
} from '@magicschool/spellbook/components/TextInput/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

export type TextInputFieldTypes =
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'hidden'
  | 'image'
  | 'month'
  | 'number'
  | 'password'
  | 'range'
  | 'reset'
  | 'search'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';

export type TextInputProps = Omit<React.ComponentProps<'input'>, 'size' | 'type'> & // size is used for styling, not the default HTML attribute
  Omit<VariantProps<typeof textInputVariants>, 'invalid'> & {
    /** Associates the input with an external label via `aria-labelledby` when no `id` is provided. */
    labelId?: string;
    /** The allowed type of the TextInput component. */
    type?: TextInputFieldTypes;
    /** Applies error border styling. Pass as a boolean, not the string "true". Pair with `aria-describedby` pointing to an error message element. */
    invalid?: boolean;
  };

export const textInputVariants = cva(
  [
    'flex',
    'items-center',
    'justify-start',
    'w-full',
    'sm:min-w-form-field-min',
    'border',
    'text-sm',
    'font-normal',
    'transition-all',
    'outline-none',
    'appearance-none',
    'cursor-text',
    'group/text-input',
  ],
  {
    variants: {
      /** The size of the input. */
      size: {
        md: ['h-control-h-md', 'rounded-control-md', 'px-2', 'gap-1'],
        lg: ['h-control-h-lg', 'rounded-control-lg', 'px-3', 'gap-2'],
        xl: ['h-control-h-xl', 'rounded-control-xl', 'px-3', 'gap-2'],
      },
      /** Whether the input is read-only. */
      readOnly: {
        true: [
          'bg-surface-background-1',
          'border-border-default',
          'hover:border-border-default',
          'focus-within:border-border-default',
          'hover:focus-within:border-border-default',
        ],
        false: [
          'bg-surface-background-0',
          'border-border-strong',
          'hover:border-border-strong-hover',
          'focus-within:border-focus-ring',
          'hover:focus-within:border-focus-ring',
        ],
      },
      /** Whether the textInput is disabled. */
      disabled: {
        true: [
          'cursor-not-allowed',
          'border-disabled-background',
          'bg-disabled-background',
          'hover:border-disabled-background',
          'text-disabled-foreground',
        ],
      },
      /** Whether the field is invalid */
      invalid: {
        true: [
          /**
           * @TODO [SPE-105] Centralize "invalid"/state color tokens to single utility
           * https://linear.app/magicschool/issue/SPE-105/centralize-invalid-state-color-tokens
           */
          'border-error-stroke',
          'hover:border-error-stroke',
          'focus-within:border-error-stroke',
          'hover:focus-within:border-error-stroke',
        ],
      },
    },
    compoundVariants: [
      {
        invalid: true,
        disabled: true,
        className: 'border-disabled-background hover:border-disabled-background focus-within:border-disabled-background',
      },
    ],
  },
);

const textInputFieldVariants = cva(
  [
    'cursor-text',
    'text-default-text-strong',
    'focus-visible:outline-0',
    'w-full',
    'h-full',
    'bg-transparent',
    'placeholder:text-placeholder',
    'transition-colors',
    'group-hover/text-input:placeholder:text-placeholder-hover',
    'disabled:cursor-not-allowed',
    'disabled:placeholder:text-disabled-foreground',
    'disabled:text-disabled-foreground',
  ],
  {
    variants: {
      readOnly: {
        true: 'placeholder:text-disabled-foreground',
      },
    },
  },
);

const textInputLeadingAddOnVariants = cva(
  [
    'items-center',
    'justify-center',
    'inline-flex',
    'shrink-0',
    'h-full',
    'ltr:border-r-1',
    'ltr:border-r-border-strong',
    'rtl:border-l-1',
    'rtl:border-l-border-strong',
  ],
  {
    variants: {
      size: {
        md: ['ltr:pr-2', 'rtl:pl-2'],
        lg: ['ltr:pr-3', 'rtl:pl-3'],
        xl: ['ltr:pr-3', 'rtl:pl-3'],
      },
      disabled: {
        true: ['ltr:border-r-disabled-background', 'rtl:border-l-disabled-background'],
      },
    },
  },
);

const textInputTrailingAddOnVariants = cva(
  [
    'items-center',
    'justify-center',
    'inline-flex',
    'shrink-0',
    'h-full',
    'rtl:border-r-1',
    'rtl:border-r-border-strong',
    'ltr:border-l-1',
    'ltr:border-l-border-strong',
  ],
  {
    variants: {
      size: {
        md: ['ltr:pl-2', 'rtl:pr-2'],
        lg: ['ltr:pl-3', 'rtl:pr-3'],
        xl: ['ltr:pl-3', 'rtl:pr-3'],
      },
      disabled: {
        true: ['ltr:border-l-disabled-background', 'rtl:border-r-disabled-background'],
      },
    },
  },
);

const PICKER_TYPES = new Set(['time', 'datetime-local', 'month', 'week']);

const focusAndOpenPicker = (input: HTMLInputElement | null) => {
  if (!input) return;
  input.focus();
  if (!PICKER_TYPES.has(input.type) || input.disabled || input.readOnly) return;
  try {
    input.showPicker?.();
  } catch {
    // Browsers without a picker, or that refuse it here, still leave the input focused for typing.
  }
};

const TextInputBase = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      autoComplete = 'off',
      children,
      className,
      disabled = false,
      id,
      labelId,
      readOnly = false,
      invalid = false,
      size = 'md',
      type = 'text',
      placeholder,
      autoFocus = false,
      ...props
    },
    forwardedRef,
  ) => {
    const ref = useRef<HTMLInputElement | null>(null);
    const { slots } = useSlots(children, {
      leadingAddOn: { Component: LeadingAddOnSlot },
      leadingAdornment: { Component: LeadingAdornmentSlot, props: { ...(disabled && { color: 'disabled' }) } },
      trailingAdornment: { Component: TrailingAdornmentSlot, props: { ...(disabled && { color: 'disabled' }) } },
      trailingAddOn: { Component: TrailingAddOnSlot },
    });

    const focusInputFromContainer = (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      // A click on the input itself must keep its default action, which opens native date and time pickers on mobile.
      if (event.target instanceof HTMLInputElement) return;
      event.preventDefault();
      const input = event.currentTarget.querySelector('input');
      if (event.target instanceof Element && event.target.closest('button')) input?.focus();
      else focusAndOpenPicker(input);
    };

    const focusInputFromAddOn = (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      event.stopPropagation();
      focusAndOpenPicker(event.currentTarget.parentElement?.querySelector('input') ?? null);
    };

    useImperativeHandle(forwardedRef, () => ref.current!, []);

    useEffect(() => {
      if (ref.current && autoFocus && !disabled) {
        requestAnimationFrame(() => {
          ref.current?.focus();
        });
      }
    }, [autoFocus, disabled, ref]);

    if (type === 'hidden') {
      return <input type="hidden" {...props} />;
    }

    return (
      // biome-ignore lint/a11y/useKeyWithClickEvents: container click is only used to forward focus to the input
      <div
        data-spellbook-name="text-input"
        className={cn(textInputVariants({ disabled, size, readOnly, invalid }), className)}
        onClick={focusInputFromContainer}
      >
        {slots.leadingAddOn && (
          // biome-ignore lint/a11y/useKeyWithClickEvents: container click is only used to forward focus to the input
          <span className={textInputLeadingAddOnVariants({ size, disabled })} onClick={focusInputFromAddOn}>
            {slots.leadingAddOn}
          </span>
        )}
        {slots.leadingAdornment}
        <input
          autoComplete={autoComplete}
          className={textInputFieldVariants({ readOnly })}
          type={type}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          aria-invalid={invalid ?? undefined}
          aria-readonly={readOnly ?? undefined}
          aria-disabled={disabled ?? undefined}
          ref={ref}
          id={id}
          {...(labelId && !id && { 'aria-labelledby': labelId })}
          {...props}
        />
        {slots.trailingAdornment}
        {slots.trailingAddOn && (
          // biome-ignore lint/a11y/useKeyWithClickEvents: container click is only used to forward focus to the input
          <span className={textInputTrailingAddOnVariants({ size, disabled })} onClick={focusInputFromAddOn}>
            {slots.trailingAddOn}
          </span>
        )}
      </div>
    );
  },
);

/**
 * Single-line text input for forms, supporting leading/trailing add-ons and adornments.
 *
 * ### When to use
 * - Form fields requiring single-line text entry (text, email, password, number, URL, etc.)
 * - Inputs that benefit from visual context via add-ons (e.g., currency symbols) or adornments (e.g., icons)
 *
 * ### When NOT to use
 * - Multi-line text entry: use `Textarea` instead
 * - Search fields with built-in clear and icon behavior: use `SearchInput` instead
 * - Rich text or markdown editing: use a rich text editor component instead
 *
 * ### API
 * - `size`: Input dimensions: `md` (default) for standard forms, `lg` for prominent inputs, `xl` for extra-large inputs
 * - `type`: HTML input type. Supports `text`, `email`, `password`, `number`, `search`, `url`, `tel`, `date`, and more
 * - `invalid`: Applies error border styling. Pair with `aria-describedby` pointing to an error message
 * - `readOnly`: Renders a non-editable input with muted styling
 * - `labelId`: Associates the input with an external label via `aria-labelledby` when no `id` is provided
 *
 * ### Composition (slots)
 * - `<TextInput.LeadingAddOn>`: Bordered region before the input. Accepts `LabelProps`. Only include when adding contextual text (e.g., currency symbol) — not rendered by default.
 * - `<TextInput.LeadingAdornment>`: Icon before the input. Accepts `IconProps`. Only include when adding a visual indicator — not rendered by default.
 * - `<TextInput.TrailingAdornment>`: Icon after the input. Accepts `IconProps`. Only include when adding an action or indicator — not rendered by default.
 * - `<TextInput.TrailingAddOn>`: Bordered region after the input. Accepts `LabelProps`. Only include when adding contextual text (e.g., unit) — not rendered by default.
 *
 * ### Visual states
 * - **Default**: Standard border, white background
 * - **Hover**: Border darkens to `border-hover`
 * - **Focus**: Border changes to `focus-ring`
 * - **Invalid**: Error-colored border, sets `aria-invalid="true"`
 * - **Disabled**: Muted background, `cursor-not-allowed`, sets `aria-disabled="true"`
 * - **Read-only**: Subtle background, sets `aria-readonly="true"`
 *
 * ### Accessibility
 * - MUST have a visible `<Label>` via `Form.Group` or a standalone `<Label htmlFor={id}>`. Use `sr-only` class only when a visible label is not possible.
 * - Error states should pair `invalid` with `aria-describedby` referencing the error message element
 * - When no `id` is set, provide `labelId` to associate with an external label via `aria-labelledby`
 */
export const TextInput = Object.assign(TextInputBase, {
  LeadingAddOn: LeadingAddOnSlot,
  LeadingAdornment: LeadingAdornmentSlot,
  TrailingAdornment: TrailingAdornmentSlot,
  TrailingAddOn: TrailingAddOnSlot,
});
